import { useQueryClient } from '@tanstack/react-query';
import { CUSTOM_TEMPLATES_BASE_QUERY_KEY } from 'components/domain/automation/customTemplates/customTemplatesQuery';
import { TransLink } from 'components/text/TransLink';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

export const DUPLICATE_CUSTOM_TEMPLATE_MUTATION_KEY = 'duplicateCustomTemplate';

const useDuplicateCustomTemplate = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotify();
  const navigate = useNavigate();

  const { mutateAsync } = useAppMutation({
    mutationKey: [DUPLICATE_CUSTOM_TEMPLATE_MUTATION_KEY],
    mutationFn: async ({
      customTemplateUid,
    }: {
      customTemplateUid: string;
    }) => {
      const response = await API.post<{ newTemplateUid: string }>(
        `/api/internal/automation/custom-templates/${customTemplateUid}/duplicate`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CUSTOM_TEMPLATES_BASE_QUERY_KEY]);
    },
    onError: (error: any) => {
      notifyError(
        getErrorMessage({
          t,
          baseKey: 'pageAutomations.customTemplates.apiErrorCodes.',
          apiErrorCode: error.response?.data?.apiErrorCode,
          apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        }),
      );
    },
  });

  const handleNavigateToNewCustomTemplate = (uid: string) => {
    navigate(`./${uid}`);
  };

  const duplicateCustomTemplate = async ({
    customTemplateUid,
  }: {
    customTemplateUid: string;
  }) => {
    const { newTemplateUid } = await mutateAsync({
      customTemplateUid,
    });

    if (newTemplateUid) {
      notifySuccess(
        <Trans
          i18nKey="pageAutomations.customTemplates.duplicateSuccessLink"
          components={{
            link1: (
              <TransLink
                data-testid="duplicate-custom-template-success-link"
                onClick={() =>
                  handleNavigateToNewCustomTemplate(newTemplateUid)
                }
              />
            ),
          }}
        />,
      );
    }
  };

  return {
    duplicateCustomTemplate,
  };
};

export default useDuplicateCustomTemplate;
